import { useContext } from 'react'
import swal from 'sweetalert2'

import {
  Box, Button, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useOrderBundle, { useOrderBundleForm } from '@hooks/useOrderBundle'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { OrderBundleModel } from '@models/types'

interface ConfirmBundleDuplicatePayload {
  closeModal: () => void,
  duplicateFn: Function,
  duplicateOrderBundle: (payload: object) => Promise<object>,
  entityState: Partial<OrderBundleModel>,
}

const confirmDuplicateBundle = (params: ConfirmBundleDuplicatePayload) => {
  const {
    closeModal, duplicateFn, duplicateOrderBundle, entityState,
  } = params

  const duplicateOrderBundlePayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Bundle Duplicated',
    shouldRedirect: true,
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, duplicate it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'This will duplicate the order bundle',
      title: 'Duplicate Bundle?',
    })
    .then(({ value }) => {
      if (value){
        duplicateOrderBundle(duplicateOrderBundlePayload)
      }
    })
}

type ModalCallbacks = {
  closeModal: () => void,
  duplicateOrderBundle: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const DuplicateOrderBundleModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'DuplicateOrderBundleModal', showModal } = props
  const { closeModal, duplicateOrderBundle } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { orderBundle } = digObject(modalData, modalKey, {})

  const orderBundlePayload = useOrderBundle(orderBundle)
  const {
    callbacks: {
      duplicateOrderBundle: duplicateFn,
    },
    creating,
  } = orderBundlePayload

  const orderBundleForm = useOrderBundleForm({
    ...orderBundle,
    title: `${orderBundle.title} Copy`,
  })

  const {
    entityState,
    entityState: { title },
    handlers,
    saveEnabled,
  } = orderBundleForm

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Duplicate"
        titleSecondLine="Bundle"
      />

      <SidebarModal.SubHeader boxProps={{ flexDirection: 'column' }}>
        <Text color="mysteryGrey" fontSize="small" width="100%">
          Duplicate Source
        </Text>

        <Box flexDirection="column" marginTop="large">
          <Text color="bodyFontColor" marginBottom="medium">{orderBundle.title}</Text>
          <Text color="mysteryGrey" fontSize="xsmall">{`Bundle From ${orderBundle.title}`}</Text>
        </Box>
      </SidebarModal.SubHeader>

      <SidebarModal.Content>
        <Form>
          <Form.Field label="New Title">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => confirmDuplicateBundle({
            // @ts-expect-error TS2322
            closeModal, duplicateFn, duplicateOrderBundle, entityState,
          })}
          size="large"
        >
          Duplicate Bundle
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicateOrderBundleModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
